import React, { useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader/Loader";
import Step0UserSelection from "./steps/Step0UserSelection";
import Step1BusinessInfo from "./steps/Step1BusinessInfo";
import Step2CampaignDetails from "./steps/Step2CampaignDetails";
import Step3FinancialInfo from "./steps/Step3FinancialInfo";
import Step4FilesMedia from "./steps/Step4FilesMedia";
import Step5ReviewSubmit from "./steps/Step5ReviewSubmit";
import "./CreateCampaign.scss";

const STEPS = [
  { id: 0, title: "User Selection", description: "Select or create user" },
  { id: 1, title: "Business Info", description: "Company details" },
  { id: 2, title: "Campaign Details", description: "Campaign information" },
  { id: 3, title: "Financial Info", description: "Financial details" },
  { id: 4, title: "Files & Media", description: "Upload files" },
  { id: 5, title: "Review & Submit", description: "Review and create" },
];

const CreateCampaign = () => {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const token = localStorage.getItem("accessToken");

  // Form data state - accumulates data from all steps
  const [formData, setFormData] = useState({
    // Step 0: User Selection
    user_id: null,
    user_email: "",
    first_name: "",
    last_name: "",
    isNewUser: false,

    // Step 1: Business Info
    corporation_legal_name: "",
    business_category: "",
    website: "",
    city: "",
    state_id: "",
    country_id: "",

    // Step 2: Campaign Details
    campaign_title: "",
    campaign_description: "",
    raise_goal: "",
    minimum_investment: "",
    campaign_type: "1", // Default to Equity
    campaign_for: "Start Up",
    equity_percentage: "",
    interest_rate: "",
    maturity_months: "",
    start_date: "",
    end_date: "",
    // Fund-specific details
    fund_type_description: "",
    one_line_description: "",
    risk_profile: "",
    investment_horizon: "",
    target_arr: "",
    fund_summary: "",
    key_reasons_to_invest: "",
    company_overview: "",

    // Step 3: Financial Info
    target_raise: "",
    revenue_last_year: "",
    valuation: "",
    number_of_employees: "",
    year_founded: "",

    // Step 4: Files & Media
    logo: null,
    banner: null,
    business_plan: null,

    // Step 3: Banking Info
    bank_name: "",
    account_name: "",
    account_number: "",
    routing_number: "",

    // Nature of Business (multi-select)
    nature_of_business: [],
  });

  // File preview URLs
  const [filePreviews, setFilePreviews] = useState({
    logo: null,
    banner: null,
    business_plan: null,
  });

  const updateFormData = (stepData) => {
    setFormData((prev) => ({ ...prev, ...stepData }));
  };

  const updateFilePreview = (field, url) => {
    setFilePreviews((prev) => ({ ...prev, [field]: url }));
  };

  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step) => {
    if (step >= 0 && step < STEPS.length) {
      setCurrentStep(step);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      // Build FormData for multipart/form-data submission
      const submitData = new FormData();

      // User info - map to backend expected field names
      if (formData.user_id) {
        submitData.append("user_id", formData.user_id);
      }
      submitData.append("user_email", formData.user_email);
      submitData.append("user_firstname", formData.first_name); // Backend expects "user_firstname"
      submitData.append("user_lastname", formData.last_name); // Backend expects "user_lastname"
      // Flag to create user if not exists (for new users)
      if (formData.isNewUser) {
        submitData.append("create_user_if_not_exists", "true");
      }

      // Business info - map to backend expected field names
      submitData.append("corporation_legal_name", formData.corporation_legal_name);
      submitData.append("business_category", formData.business_category);
      submitData.append("website", formData.website || "");
      submitData.append("city", formData.city);
      submitData.append("province", formData.state_id); // Backend expects "province"
      submitData.append("country_id", formData.country_id);

      // Campaign details - map to backend expected field names
      submitData.append("campaign_name", formData.campaign_title); // Backend expects "campaign_name"
      const descriptionValue =
        formData.campaign_type === "3"
          ? formData.fund_summary || formData.campaign_description || ""
          : formData.campaign_description;
      submitData.append("details", descriptionValue); // Backend expects "details"
      submitData.append("goal_invest_amnt", formData.raise_goal); // Backend expects "goal_invest_amnt"
      submitData.append("minimum_investment", formData.minimum_investment);
      const campaignTypeValue =
        formData.campaign_type === "1"
          ? "EQUITY"
          : formData.campaign_type === "2"
          ? "LENDME"
          : "FUND";
      submitData.append("type", campaignTypeValue);
      submitData.append("campaign_for", formData.campaign_for || "");

      if (formData.campaign_type === "1") {
        submitData.append("equity_percentage", formData.equity_percentage || "");
      } else if (formData.campaign_type === "2") {
        submitData.append("interest_rate", formData.interest_rate || "");
        submitData.append("loan_duration_in_month", formData.maturity_months || ""); // Backend expects "loan_duration_in_month"
      } else if (formData.campaign_type === "3") {
        submitData.append("fund_name", formData.campaign_title || "");
        submitData.append("fund_type_description", formData.fund_type_description || "");
        submitData.append("one_line_description", formData.one_line_description || "");
        submitData.append("risk_profile", formData.risk_profile || "");
        submitData.append("investment_horizon", formData.investment_horizon || "");
        submitData.append("target_arr", formData.target_arr || "");
        submitData.append("fund_summary", formData.fund_summary || formData.campaign_description || "");
        submitData.append("key_reasons_to_invest", formData.key_reasons_to_invest || "");
        submitData.append("company_overview", formData.company_overview || "");
      }

      submitData.append("start_date", formData.start_date);
      submitData.append("end_date", formData.end_date);

      // Financial info - map to backend expected field names
      submitData.append("target_raise", formData.target_raise);
      submitData.append("revenue_last_year", formData.revenue_last_year || "");
      submitData.append("company_valuation", formData.valuation || ""); // Backend expects "company_valuation"
      submitData.append("number_of_employees", formData.number_of_employees || "");
      submitData.append("year_founded", formData.year_founded || "");

      // Banking info - map to backend expected field names
      submitData.append("bank_name", formData.bank_name || "");
      submitData.append("account_holder", formData.account_name || ""); // Backend expects "account_holder"
      submitData.append("account_number", formData.account_number || "");
      submitData.append("transit_number", formData.routing_number || ""); // Backend expects "transit_number"

      // Nature of business - backend expects "nature_of_business_ids"
      if (formData.nature_of_business && formData.nature_of_business.length > 0) {
        submitData.append("nature_of_business_ids", JSON.stringify(formData.nature_of_business));
      }

      // Files - map to backend expected field names
      if (formData.logo) {
        submitData.append("campaign_logo", formData.logo); // Backend expects "campaign_logo"
      }
      if (formData.banner) {
        submitData.append("banner_img", formData.banner); // Backend expects "banner_img"
      }
      if (formData.business_plan) {
        submitData.append("business_plan", formData.business_plan);
      }

      const config = {
        method: "post",
        url: process.env.REACT_APP_API + "admin/create-deal-for-user",
        headers: {
          "x-access-token": token,
          "Content-Type": "multipart/form-data",
        },
        data: submitData,
      };

      const response = await axios(config);

      if (response.data.status === 200 || response.data.status === 201) {
        setLoading(false);
        Swal.fire({
          title: "Campaign Created Successfully!",
          text: response.data.message || "The campaign has been created and published.",
          timer: 3000,
          timerProgressBar: true,
          icon: "success",
          confirmButtonText: "OK",
          showCloseButton: true,
        });
        navigate("/active-campaign");
      } else {
        throw new Error(response.data.message || "Failed to create campaign");
      }
    } catch (error) {
      setLoading(false);
      console.error("Error creating campaign:", error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || error.message || "Failed to create campaign",
        icon: "error",
        confirmButtonText: "OK",
        showCloseButton: true,
      });
    }
  };

  const renderStep = () => {
    const commonProps = {
      formData,
      updateFormData,
      nextStep,
      prevStep,
      token,
    };

    switch (currentStep) {
      case 0:
        return <Step0UserSelection {...commonProps} />;
      case 1:
        return <Step1BusinessInfo {...commonProps} />;
      case 2:
        return <Step2CampaignDetails {...commonProps} />;
      case 3:
        return <Step3FinancialInfo {...commonProps} />;
      case 4:
        return (
          <Step4FilesMedia
            {...commonProps}
            filePreviews={filePreviews}
            updateFilePreview={updateFilePreview}
          />
        );
      case 5:
        return (
          <Step5ReviewSubmit
            {...commonProps}
            filePreviews={filePreviews}
            goToStep={goToStep}
            onSubmit={handleSubmit}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="create-campaign">
      <Loader isLoading={loading} />

      <div className="create-campaign-header">
        <h1>Create Campaign for User</h1>
        <p>Create a new campaign on behalf of a user (bypasses KYC verification)</p>
      </div>

      {/* Progress Stepper */}
      <div className="stepper">
        {STEPS.map((step, index) => (
          <div
            key={step.id}
            className={`step ${index === currentStep ? "active" : ""} ${
              index < currentStep ? "completed" : ""
            }`}
            onClick={() => index < currentStep && goToStep(index)}
          >
            <div className="step-number">
              {index < currentStep ? (
                <span className="checkmark">&#10003;</span>
              ) : (
                index + 1
              )}
            </div>
            <div className="step-info">
              <span className="step-title">{step.title}</span>
              <span className="step-description">{step.description}</span>
            </div>
            {index < STEPS.length - 1 && <div className="step-connector" />}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="step-content">{renderStep()}</div>
    </div>
  );
};

export default CreateCampaign;
